/**
 * BackorderLiveInventoryPage
 * --------------------------
 * Public page: /backorders/live-inventory
 * Renders the current Sterile Mate "Hard-to-Find Live Backorder Inventory" list
 * that admins upload via the admin dashboard. Fully searchable, supports bulk
 * selection so hospitals can submit a single RFQ for many SKUs at once, and
 * still offers the "Email me the list" fallback.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search, Mail, Loader2, PackageSearch, Clock, Send, ArrowLeft, ArrowRight, X,
} from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { buildCategoryChips, classifyRow } from "@/lib/liveInventoryCategories";

const API = process.env.REACT_APP_BACKEND_URL;

// Column-name resolvers so the "Request Quote" hand-off stays reliable even
// if the daily CSV headers change slightly between uploads.
const NAME_KEYS = ["Product Name","Product","Description","Item","Item Description","Product Description","Name"];
const SKU_KEYS  = ["MFR SKU","Manufacturer SKU","SKU","Item #","Item Number","Catalog #","Cat #","Catalog Number","Part #","Part Number"];
const MFR_KEYS  = ["Manufacturer","MFR","Brand","Mfr","Vendor","Supplier"];
const QTY_KEYS  = ["Quantity","Qty","QOH","Available","On Hand"];
const pick = (row, keys) => {
  for (const k of keys) {
    const hit = Object.keys(row).find(h => h.toLowerCase().trim() === k.toLowerCase());
    if (hit && String(row[hit] || "").trim()) return String(row[hit]).trim();
  }
  return "";
};

// Stable row identity — used by the selection Set so re-renders preserve state.
const rowKey = (r, idx) => `${pick(r, MFR_KEYS)}|${pick(r, SKU_KEYS)}|${pick(r, NAME_KEYS)}|${idx}`;

export default function BackorderLiveInventoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ columns: [], rows: [], updated_at: null, empty: true });
  const [q, setQ] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [activeCats, setActiveCats] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/live-inventory`);
        setData(r.data || { columns: [], rows: [] });
      } catch (e) {
        toast.error(`Could not load list: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chips = useMemo(() => buildCategoryChips(data.rows || []), [data.rows]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = data.rows || [];
    if (activeCats.size > 0) out = out.filter(r => activeCats.has(classifyRow(r)));
    if (term) {
      out = out.filter(r =>
        Object.values(r).some(v => String(v || "").toLowerCase().includes(term))
      );
    }
    return out;
  }, [q, data.rows, activeCats]);

  const toggleCat = (id) => setActiveCats(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const lastUpdated = data.updated_at
    ? new Date(data.updated_at).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "Not yet uploaded";

  const toggleRow = (key) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const allVisibleKeys = useMemo(() => rows.map((r, i) => rowKey(r, i)), [rows]);
  const allVisibleSelected = allVisibleKeys.length > 0 && allVisibleKeys.every(k => selected.has(k));
  const toggleSelectAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) allVisibleKeys.forEach(k => next.delete(k));
      else allVisibleKeys.forEach(k => next.add(k));
      return next;
    });
  };

  // Hand off the selected rows to the quick-quote form on the landing page.
  const submitSelected = () => {
    if (selected.size === 0) return;
    const items = rows
      .map((r, i) => ({ row: r, key: rowKey(r, i) }))
      .filter(({ key }) => selected.has(key))
      .map(({ row }) => ({
        product_name: pick(row, NAME_KEYS),
        sku: pick(row, SKU_KEYS),
        manufacturer: pick(row, MFR_KEYS),
        quantity: pick(row, QTY_KEYS) || "",
        notes: "",
      }))
      .filter(x => x.product_name);
    if (items.length === 0) {
      toast.error("Selected rows don't have a product name we can prefill.");
      return;
    }
    try {
      sessionStorage.setItem("bulk_quote_items", JSON.stringify(items));
    } catch { /* private-mode/quota — best-effort */ }
    navigate(`/?bulk=${items.length}#quick-quote`);
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="live-inventory-page">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-cyan-100 via-white to-amber-100/60 border-b border-slate-200">
        <div className="max-w-[110rem] mx-auto px-6 lg:px-10 py-14 lg:py-16">
          <div className="flex items-center gap-3 mb-5 text-sm">
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate("/how-it-works/backorder-sourcing");
              }}
              className="inline-flex items-center gap-1.5 text-cyan-800 hover:text-cyan-900 font-semibold"
              data-testid="live-inv-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <span className="text-slate-400">·</span>
            <Link to="/" className="text-slate-700 hover:text-slate-900">Home</Link>
            <span className="text-slate-400">/</span>
            <Link to="/how-it-works/backorder-sourcing" className="text-slate-700 hover:text-slate-900">Backorder Sourcing</Link>
            <span className="text-slate-400">/</span>
            <span className="text-slate-900 font-semibold">Weekly Deals &amp; Live Inventory</span>
          </div>

          <div className="inline-flex items-center gap-2 bg-amber-200 ring-1 ring-amber-400 px-3 py-1.5 rounded-full mb-4 shadow-sm">
            <PackageSearch className="w-3.5 h-3.5 text-amber-900" />
            <span className="text-[11px] font-black uppercase tracking-[0.15em] text-amber-900">
              Weekly Deals &amp; Hard-to-Find Live Inventory
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight text-slate-900">
            Live Backorder List <span className="text-cyan-800">— Search Any Time</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base md:text-lg text-slate-700 leading-relaxed">
            Every SKU our national supplier partners flag as regional or national backorder,
            aggregated into one searchable list. Refreshed daily.
          </p>
          <p className="mt-2 inline-flex items-center gap-2 text-sm text-slate-600">
            <Clock className="w-3.5 h-3.5" /> Last updated: <strong className="text-slate-900">{lastUpdated}</strong>
            {data.row_count ? <span className="ml-2 text-slate-500">· {data.row_count} items</span> : null}
          </p>
        </div>
      </section>

      {/* Search + Email row */}
      <section className="max-w-[110rem] mx-auto px-6 lg:px-10 py-8">
        {chips.length > 0 && (
          <div className="mb-4" data-testid="live-inv-category-chips">
            <p className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-500 mb-2">
              Browse by category
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCats(new Set())}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold ring-1 transition-colors ${
                  activeCats.size === 0
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "bg-white text-slate-700 ring-slate-300 hover:ring-slate-500 hover:text-slate-900"
                }`}
                data-testid="live-inv-chip-all"
              >
                All items
                <span className="ml-1.5 opacity-70">{data.row_count || 0}</span>
              </button>
              {chips.map(c => {
                const on = activeCats.has(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCat(c.id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold ring-1 transition-colors ${
                      on
                        ? "bg-amber-500 text-slate-900 ring-amber-600"
                        : "bg-white text-slate-700 ring-slate-300 hover:ring-amber-500 hover:text-amber-900"
                    }`}
                    data-testid={`live-inv-chip-${c.id}`}
                  >
                    {c.label}
                    <span className="ml-1.5 opacity-70">{c.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by product name, manufacturer, category, SKU…"
              className="pl-10 h-12 text-base bg-white border-slate-400 text-slate-900 placeholder:text-slate-500 shadow-sm focus-visible:ring-cyan-500"
              data-testid="live-inv-search"
            />
          </div>
          <Button
            variant="outline"
            className="h-12 px-5 border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100 font-bold"
            onClick={() => setEmailOpen(v => !v)}
            data-testid="live-inv-email-toggle"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email me the list
          </Button>
        </div>

        {emailOpen && (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4 shadow-sm">
            <EmailRequestForm onDone={() => setEmailOpen(false)} />
          </div>
        )}
      </section>

      {/* Table */}
      <section className="max-w-[110rem] mx-auto px-6 lg:px-10 pb-32">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-16">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading the list…
          </div>
        ) : data.empty ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
            <PackageSearch className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-semibold">The list hasn&rsquo;t been uploaded yet.</p>
            <p className="text-slate-500 text-sm mt-1">
              Check back shortly or email <a className="text-cyan-700 underline" href="mailto:orders@sterilemate.com">orders@sterilemate.com</a> and we&rsquo;ll send the latest one over.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
            <p className="text-slate-600">
              {q
                ? <>No matches for &ldquo;<b>{q}</b>&rdquo;{activeCats.size > 0 ? " in the selected categories" : ""}.</>
                : "No items in the selected categories."}
            </p>
            {activeCats.size > 0 && (
              <button
                type="button"
                onClick={() => setActiveCats(new Set())}
                className="mt-3 text-sm font-bold text-cyan-800 hover:text-cyan-900 underline"
                data-testid="live-inv-clear-cats"
              >
                Clear category filters
              </button>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-300 overflow-hidden shadow-md bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="live-inv-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="w-10 pl-4 py-3">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        aria-label="Select all visible rows"
                        className="w-4 h-4 accent-amber-500 cursor-pointer"
                        data-testid="live-inv-select-all"
                      />
                    </th>
                    {data.columns.map(col => (
                      <th key={col} className="text-left font-bold text-[11px] uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                    <th className="text-right font-bold text-[11px] uppercase tracking-wider px-4 py-3 whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {rows.map((r, idx) => {
                    const key = rowKey(r, idx);
                    const isChecked = selected.has(key);
                    const p = new URLSearchParams();
                    const nm = pick(r, NAME_KEYS); if (nm) p.set("prefill_name", nm);
                    const sk = pick(r, SKU_KEYS); if (sk) p.set("prefill_sku", sk);
                    const mf = pick(r, MFR_KEYS); if (mf) p.set("prefill_mfr", mf);
                    const href = `/?${p.toString()}#quick-quote`;
                    return (
                      <tr
                        key={key}
                        className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-cyan-50 transition-colors ${isChecked ? "!bg-amber-50" : ""}`}
                      >
                        <td className="w-10 pl-4 py-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRow(key)}
                            aria-label={`Select ${nm || "row"}`}
                            className="w-4 h-4 accent-amber-500 cursor-pointer"
                            data-testid={`live-inv-select-row-${idx}`}
                          />
                        </td>
                        {data.columns.map(col => (
                          <td
                            key={col}
                            className="px-4 py-3 text-slate-800 whitespace-nowrap max-w-[260px] truncate"
                            title={r[col]}
                          >
                            {r[col] || <span className="text-slate-400">—</span>}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <a
                            href={href}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 text-slate-900 text-xs font-black hover:bg-amber-400 shadow-sm transition-colors"
                            data-testid={`live-inv-full-request-quote-${idx}`}
                            title="Add this item to the backorder request form"
                          >
                            <Send className="w-3 h-3" />
                            Request Quote
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-100 border-t border-slate-300 px-4 py-2 text-xs text-slate-700 flex justify-between">
              <span className="font-semibold">{rows.length} of {data.row_count} items shown</span>
              <span className="italic text-slate-500">Tip: check multiple rows to submit one bulk RFQ</span>
            </div>
          </div>
        )}
      </section>

      {/* Sticky bulk-action bar — shows only when items are selected */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-amber-400/50 bg-slate-950/95 backdrop-blur shadow-[0_-16px_40px_-12px_rgba(0,0,0,0.6)]"
          data-testid="live-inv-bulk-bar"
        >
          <div className="max-w-[110rem] mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-white font-bold">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-slate-900 text-sm font-black">
                {selected.size}
              </span>
              <span className="text-sm">item{selected.size === 1 ? "" : "s"} selected</span>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={clearSelection}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-slate-300 hover:text-white text-sm font-semibold"
              data-testid="live-inv-bulk-clear"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
            <button
              type="button"
              onClick={submitSelected}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 font-black text-sm shadow-md ring-1 ring-amber-600/30"
              data-testid="live-inv-bulk-submit"
            >
              <Send className="w-4 h-4" />
              Request Quote for {selected.size} item{selected.size === 1 ? "" : "s"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Single "Submit Backorder Request" pill — hidden while the bulk bar
          is up so they don't stack. */}
      {selected.size === 0 && (
        <a
          href="/#quick-quote"
          className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-black text-sm shadow-[0_16px_50px_-12px_rgba(245,158,11,0.55)] ring-1 ring-amber-600/30 transition-all hover:scale-105"
          data-testid="live-inv-sticky-submit"
        >
          <Send className="w-4 h-4" />
          Submit Backorder Request
        </a>
      )}
    </div>
  );
}

function EmailRequestForm({ onDone }) {
  const [form, setForm] = useState({ email: "", name: "", hospital: "", note: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.email) {
      toast.error("Add your email so we know where to send it.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/live-inventory/request`, form);
      toast.success(r.data?.message || "Request sent.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Input placeholder="Your email *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="live-inv-email-email" />
      <Input placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="live-inv-email-name" />
      <Input placeholder="Hospital / health system" value={form.hospital} onChange={e => setForm(f => ({ ...f, hospital: e.target.value }))} data-testid="live-inv-email-hospital" />
      <Input placeholder="Categories of interest (surgical, anesthesia, wound care…)" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} data-testid="live-inv-email-note" />
      <div className="md:col-span-2 flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="live-inv-email-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Send me the list
        </Button>
      </div>
    </div>
  );
}
